import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import useConfig from 'common/hooks/useConfig';
import useSearchParams from 'common/hooks/useSearchParams';

import {
  createContentPage,
  deleteContentPage,
  duplicateContentPage,
  getContentPage,
  getPreviewLink,
  getSettings,
  listContentPages,
  MUTATE_ACTION,
  updateContentPage,
  updateSettings,
} from './service';

const { CREATE, EDIT, DUPLICATE, DELETE } = MUTATE_ACTION;

const mutationKeyLists = ['website-builder', 'content-pages'];

const getWebsiteBuilderBaseQueryKeyWithSuffix = (eventId, suffix) => [
  'events',
  String(eventId),
  'website-builder',
  suffix,
];

export const getContentPagesBaseQueryKey = eventId =>
  getWebsiteBuilderBaseQueryKeyWithSuffix(eventId, 'content-pages');

export const contentPageOptions = (eventId, pageId) => ({
  queryKey: [...getContentPagesBaseQueryKey(eventId), String(pageId)],
  queryFn: () => getContentPage(eventId, pageId),
  select: ({ data }) => data,
  enabled: !!pageId,
});

export const useContentPage = pageId => {
  const { eventId } = useParams();
  return useQuery(contentPageOptions(eventId, pageId));
};

export const contentPagesOptions = (eventId, search, query) => ({
  queryKey: [...getContentPagesBaseQueryKey(eventId), { search: search || '' }, query],
  queryFn: () => listContentPages(eventId, { search, ...query }),
  select: ({ data }) => data,
  keepPreviousData: true,
});

// This is used to get all content pages for the main ContentPage and Delete page modal.
// In these places we have to get all the pages regardless of search, pagination, or other query params.
export const useAllContentPagesQuery = ({ enabled } = {}) => {
  const { eventId } = useParams();
  return useQuery({
    ...contentPagesOptions(eventId, '', { limit: 1000 }),
    enabled,
  });
};

export const useContentPagesQuery = () => {
  const { eventId } = useParams();
  const query = useSearchParams();
  const search = query.get('search');

  return useQuery(contentPagesOptions(eventId, search, {}));
};

const createMutationOptions = ({ eventId }) => ({
  mutationKey: [...mutationKeyLists, 'create'],
  mutationFn: ({ payload }) => createContentPage(eventId, payload),
});

export const editMutationOptions = ({ eventId }) => ({
  mutationKey: [...mutationKeyLists, 'edit'],
  mutationFn: ({ payload, pageId }) => updateContentPage(eventId, pageId, payload),
});

export const duplicateMutationOptions = ({ eventId }) => ({
  mutationKey: [...mutationKeyLists, 'duplicate'],
  mutationFn: ({ payload, pageId }) => duplicateContentPage(eventId, pageId, payload),
});

export const deleteMutationOptions = ({ eventId }) => ({
  mutationKey: [...mutationKeyLists, 'delete'],
  mutationFn: ({ pageId }) => deleteContentPage(eventId, pageId),
});

// Preview link expires after 1 hour, but we refetch it a little
// more often than that to account for any potential latency.
const PREVIEW_LINK_REFETCH_INTERVAL = 57 * 60 * 1000;

const previewLinkQueryOptions = eventId => ({
  queryKey: [...getWebsiteBuilderBaseQueryKeyWithSuffix(eventId, 'preview-link')],
  queryFn: () => getPreviewLink(eventId),
  select: ({ data }) => data,
  refetchInterval: PREVIEW_LINK_REFETCH_INTERVAL,
});

export const usePreviewLinkQuery = () => {
  const { eventId } = useParams();
  return useQuery(previewLinkQueryOptions(eventId));
};

const settingsQueryOptions = (eventId, enabled = true) => ({
  queryKey: [...getWebsiteBuilderBaseQueryKeyWithSuffix(eventId, 'settings')],
  queryFn: () => getSettings(eventId),
  select: ({ data }) => data,
  enabled,
});

export const useSettingsQuery = () => {
  const { eventId } = useParams();
  const config = useConfig();
  const isWebsiteBuilderEnabled = config.isConfigEnabled('is_website_builder_enabled');

  const { isLoading, ...rest } = useQuery(settingsQueryOptions(eventId, isWebsiteBuilderEnabled));

  return {
    isLoading: isLoading && isWebsiteBuilderEnabled,
    ...rest,
  };
};

export const useContentPageMutations = action => {
  const { eventId } = useParams();
  const queryClient = useQueryClient();

  const mutationOptions = {
    [CREATE]: createMutationOptions,
    [EDIT]: editMutationOptions,
    [DUPLICATE]: duplicateMutationOptions,
    [DELETE]: deleteMutationOptions,
  }[action]({ eventId });

  return useMutation({
    ...mutationOptions,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsQueryOptions(eventId).queryKey });
      queryClient.invalidateQueries({ queryKey: getContentPagesBaseQueryKey(eventId) });
    },
  });
};

export const settingsMutationOptions = eventId => ({
  mutationKey: ['website-builder', 'settings', 'update'],
  mutationFn: payload => updateSettings(eventId, payload),
});

export const useSettingsMutation = () => {
  const queryClient = useQueryClient();
  const { eventId } = useParams();

  return useMutation({
    ...settingsMutationOptions(eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsQueryOptions(eventId).queryKey });
      queryClient.invalidateQueries({ queryKey: getContentPagesBaseQueryKey(eventId) });
    },
  });
};
